import { Box } from '@mui/material';
import { useState } from 'react';
import { GroupLabel, KitButton, KitCard, KitItem, KitList, KitPill, SecH } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import { MobileActionItem, MobileListRow } from '../common/MobileCrud';
import { KitBanner, KitEmpty, KitField, KitFormBar, KitInput, KitSwitch, KitTextarea } from '../common/MobileKitExtras';

/**
 * Telas mobile de Checklists (`prototipos-mobile/checklists/`): detalhe, formulário do
 * template e formulário do grupo. Só apresentação — dados, validação, chamadas e
 * permissões vêm de `pages/vehicles/checklist/{View,Form,GroupForm}.js`.
 */

const CheckIcon = protoIcon('check');
const LayersIcon = protoIcon('layers');
const EditIcon = protoIcon('edit');
const TrashIcon = protoIcon('trash');
const CopyIcon = protoIcon('copy');
const PlusIcon = protoIcon('plus');
const SortIcon = protoIcon('sort');

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// `checklists/detalhe.html` — cartão-resumo + um rótulo/lista por grupo.
export function MobileChecklistDetail({ checklist, groups, itemsCount, canEditGroup, onEditGroup }) {
    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitCard sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ fontWeight: 700 }}>{plural(itemsCount, 'item', 'itens')} · {plural(groups.length, 'grupo', 'grupos')}</Box>
                    <Box sx={{ fontSize: 12.5, color: 'text.disabled' }}>{checklist?.description || 'Sem descrição cadastrada.'}</Box>
                </Box>
                {checklist?.is_default && <KitPill tone="success">Padrão</KitPill>}
            </KitCard>

            {groups.length === 0 && (
                <KitEmpty icon={LayersIcon} title="Nenhum grupo de inspeção anexado">
                    Use &quot;Editar&quot; para anexar grupos de inspeção a este template.
                </KitEmpty>
            )}

            {groups.map((group) => {
                const items = (Array.isArray(group.group_inspection_items) ? group.group_inspection_items : [])
                    .filter((it) => it.active !== false)
                    .sort((a, b) => (a.insp_item_order || 0) - (b.insp_item_order || 0));
                return (
                    <Box key={group.group_id}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <GroupLabel>{group.group_name}</GroupLabel>
                            {canEditGroup && (
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() => onEditGroup(group.group_id)}
                                    sx={{ p: '18px 4px 8px', border: 0, background: 'none', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: 'primary.main', cursor: 'pointer', lineHeight: '15.4px' }}
                                >
                                    Editar grupo
                                </Box>
                            )}
                        </Box>
                        {items.length === 0 ? (
                            <KitCard sx={{ fontSize: 12.5, color: 'text.disabled', textAlign: 'center' }}>
                                Nenhum item de inspeção cadastrado neste grupo.
                            </KitCard>
                        ) : (
                            <KitList>
                                {items.map((item) => (
                                    <KitItem key={item.insp_item_id} icon={CheckIcon} tone="green" title={item.insp_item_name} sub="" />
                                ))}
                            </KitList>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}

// `checklists/form.html` — dados, grupos (folha de ações por linha) e "Adicionar grupo".
export function MobileChecklistForm({
    name,
    onNameChange,
    nameError,
    description,
    onDescriptionChange,
    isDefault,
    onIsDefaultChange,
    swapWarning,
    groups,
    availableGroups,
    itemsLabel,
    onAddGroup,
    onNewGroup,
    onEditGroup,
    onRemoveGroup,
    onMoveGroup,
    onCancel,
    onSave,
    saving,
}) {
    const [selected, setSelected] = useState(null);
    const [addOpen, setAddOpen] = useState(false);
    const selectedIndex = selected ? groups.findIndex((g) => g.id === selected.id) : -1;
    const closeSelected = () => setSelected(null);

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }} component="form" onSubmit={(e) => { e.preventDefault(); onSave(); }} noValidate>
            <KitField label="Nome" required help={nameError} error={Boolean(nameError)}>
                <KitInput
                    type="text"
                    value={name}
                    onChange={(e) => onNameChange(e.target.value)}
                    placeholder="Ex.: Inspeção de entrada"
                    error={Boolean(nameError)}
                    maxLength={255}
                    autoComplete="off"
                />
            </KitField>
            <KitField label="Descrição">
                <KitTextarea value={description} onChange={(e) => onDescriptionChange(e.target.value)} maxLength={255} />
            </KitField>
            <KitSwitch
                title="Padrão"
                description="Usado por padrão nas ordens de serviço"
                checked={isDefault}
                onChange={(e) => onIsDefaultChange(e.target.checked)}
            />
            {swapWarning && (
                <Box sx={{ mt: '4px' }}>
                    <KitBanner tone="info">{swapWarning}</KitBanner>
                </Box>
            )}

            <SecH title="Grupos" />
            {groups.length > 0 && (
                <KitList>
                    {groups.map((g) => (
                        <MobileListRow
                            key={g.id}
                            icon={LayersIcon}
                            title={g.name}
                            sub={itemsLabel(g.id)}
                            chevron
                            onClick={() => setSelected(g)}
                        />
                    ))}
                </KitList>
            )}
            <KitButton variant="soft" block sx={{ mt: '12px' }} onClick={() => setAddOpen(true)} startIcon={<PlusIcon size={18} />}>
                Adicionar grupo
            </KitButton>

            <KitFormBar>
                <KitButton onClick={onCancel} disabled={saving}>Cancelar</KitButton>
                <KitButton variant="primary" onClick={onSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</KitButton>
            </KitFormBar>

            <MobileSheet open={Boolean(selected)} onClose={closeSelected} title={selected?.name || ''}>
                <MobileActionItem icon={EditIcon} label="Editar grupo" onClick={() => { closeSelected(); onEditGroup(selected.id); }} />
                {selectedIndex > 0 && (
                    <MobileActionItem icon={SortIcon} label="Mover para cima" onClick={() => { closeSelected(); onMoveGroup(selected.id, -1); }} />
                )}
                {selectedIndex >= 0 && selectedIndex < groups.length - 1 && (
                    <MobileActionItem icon={SortIcon} label="Mover para baixo" onClick={() => { closeSelected(); onMoveGroup(selected.id, 1); }} />
                )}
                <MobileActionItem icon={TrashIcon} label="Remover do template" destructive chevron={false} onClick={() => { closeSelected(); onRemoveGroup(selected.id); }} />
            </MobileSheet>

            <MobileSheet open={addOpen} onClose={() => setAddOpen(false)} title="Adicionar grupo">
                {availableGroups.length > 0 ? (
                    <KitList>
                        {availableGroups.map((g) => (
                            <MobileListRow
                                key={g.id}
                                icon={LayersIcon}
                                title={g.name}
                                sub={itemsLabel(g.id)}
                                onClick={() => { setAddOpen(false); onAddGroup(g); }}
                            />
                        ))}
                    </KitList>
                ) : (
                    <Box sx={{ fontSize: 13, color: 'text.disabled', textAlign: 'center', p: '8px 0 12px' }}>
                        Todos os grupos do catálogo já foram anexados.
                    </Box>
                )}
                <MobileActionItem icon={PlusIcon} label="Novo grupo de inspeção" onClick={() => { setAddOpen(false); onNewGroup(); }} />
            </MobileSheet>
        </Box>
    );
}

// `checklists/grupo-form.html` — nome, itens (toque abre a folha de edição) e "Adicionar item".
export function MobileGroupForm({
    name,
    onNameChange,
    nameError,
    description,
    onDescriptionChange,
    items,
    onAddItem,
    onChangeItem,
    onDuplicateItem,
    onMoveItem,
    onRemoveItem,
    onCancel,
    onSave,
    saving,
}) {
    // `sheet`: { id: null } = novo item; { id } = editando.
    const [sheet, setSheet] = useState(null);
    const [draft, setDraft] = useState('');
    const [draftError, setDraftError] = useState('');
    const index = sheet && sheet.id ? items.findIndex((i) => i.insp_item_id === sheet.id) : -1;

    const openNew = () => { setDraft(''); setDraftError(''); setSheet({ id: null }); };
    const openEdit = (item) => { setDraft(item.insp_item_name || ''); setDraftError(''); setSheet({ id: item.insp_item_id }); };
    const close = () => setSheet(null);
    const commit = () => {
        const text = draft.trim();
        if (!text) {
            setDraftError('Informe o texto do item.');
            return;
        }
        if (sheet.id) onChangeItem(sheet.id, text);
        else onAddItem(text);
        close();
    };

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }} component="form" onSubmit={(e) => { e.preventDefault(); onSave(); }} noValidate>
            <KitField label="Nome do grupo" required help={nameError} error={Boolean(nameError)}>
                <KitInput type="text" value={name} onChange={(e) => onNameChange(e.target.value)} error={Boolean(nameError)} autoComplete="off" />
            </KitField>
            <KitField label="Descrição">
                <KitTextarea value={description} onChange={(e) => onDescriptionChange(e.target.value)} />
            </KitField>

            <SecH title="Itens" />
            {items.length > 0 && (
                <KitList>
                    {items.map((item) => (
                        <MobileListRow
                            key={item.insp_item_id}
                            icon={CheckIcon}
                            title={item.insp_item_name || '(sem texto)'}
                            sub="Toque para editar ou reordenar"
                            end={(
                                <Box
                                    component="button"
                                    type="button"
                                    aria-label="Remover item"
                                    onClick={(e) => { e.stopPropagation(); onRemoveItem(item.insp_item_id); }}
                                    sx={{ width: 44, height: 44, m: '-13px -13px -13px 0', border: 0, background: 'none', color: 'text.primary', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                >
                                    <TrashIcon size={18} />
                                </Box>
                            )}
                            onClick={() => openEdit(item)}
                        />
                    ))}
                </KitList>
            )}
            <KitButton variant="soft" block sx={{ mt: '12px' }} onClick={openNew} startIcon={<PlusIcon size={18} />}>
                Adicionar item
            </KitButton>

            <KitFormBar>
                <KitButton onClick={onCancel} disabled={saving}>Cancelar</KitButton>
                <KitButton variant="primary" onClick={onSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</KitButton>
            </KitFormBar>

            <MobileSheet
                open={Boolean(sheet)}
                onClose={close}
                title={sheet && sheet.id ? 'Editar item' : 'Novo item'}
                footer={(
                    <>
                        <KitButton sx={{ flex: 1 }} onClick={close}>Cancelar</KitButton>
                        <KitButton variant="primary" sx={{ flex: 1 }} onClick={commit}>Concluir</KitButton>
                    </>
                )}
            >
                <KitField label="Texto do item" required help={draftError} error={Boolean(draftError)}>
                    <KitInput
                        type="text"
                        value={draft}
                        onChange={(e) => { setDraft(e.target.value); if (draftError) setDraftError(''); }}
                        error={Boolean(draftError)}
                        placeholder="Ex.: Lataria"
                        autoComplete="off"
                    />
                </KitField>
                {sheet && sheet.id && (
                    <>
                        <MobileActionItem icon={CopyIcon} label="Duplicar item" onClick={() => { close(); onDuplicateItem(sheet.id); }} />
                        {index > 0 && <MobileActionItem icon={SortIcon} label="Mover para cima" onClick={() => { close(); onMoveItem(sheet.id, -1); }} />}
                        {index >= 0 && index < items.length - 1 && <MobileActionItem icon={SortIcon} label="Mover para baixo" onClick={() => { close(); onMoveItem(sheet.id, 1); }} />}
                    </>
                )}
            </MobileSheet>
        </Box>
    );
}
